import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { AiOutlineEye } from 'react-icons/ai';
import { useBlocker } from 'react-router-dom';
import { toast } from 'react-toastify';
import useSWR from 'swr';
import { discountService } from 'services';
import { useAuth } from 'hooks';
import { discountFactory } from 'factories';
import {
  DiscountInfo,
  DiscountsForm,
  RouterBlocker,
  Season,
  SeasonForm,
  Seasons,
} from 'types';
import { Button, Header, Loading, Main } from 'components';
import {
  DiscountConfirm,
  DiscountHistory,
  DiscountInput,
  DiscountWarningModal,
  SeasonDiscountTable,
  SeasonDiscountsModal,
} from './components';

const CONTAINER_STYLE =
  'flex flex-col p-6 border border-neutral-300 border-solid rounded-lg w-full gap-4';

function formatSeasonToForm(season: Season): SeasonForm {
  return {
    season: season.code,
    collections: season.collections.map(({ name, code, percentage }) => ({
      collection: code,
      name: name,
      discount: percentage > 0 ? String(percentage) : '',
    })),
  };
}

function formatDiscountsToForm(
  discounts: DiscountInfo | undefined = undefined
): DiscountsForm {
  const storeDiscounts = discounts ? discounts : discountFactory();

  const { percentagePastSeasons, percentageTimelessSeasons } = storeDiscounts;

  return {
    percentagePastSeasons:
      percentagePastSeasons > 0 ? String(percentagePastSeasons) : '',
    percentageTimelessSeasons:
      percentageTimelessSeasons > 0 ? String(percentageTimelessSeasons) : '',
    actualSeason: formatSeasonToForm(storeDiscounts.actualSeason),
    lastSeason: formatSeasonToForm(storeDiscounts.lastSeason),
  };
}

export function DiscountsPage() {
  const { selectedStore } = useAuth();
  const { name, brand } = selectedStore;
  const store = name || '';

  const [previousStore, setPreviousStore] = useState<string | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<Seasons | null>(null);

  const [showHistory, setShowHistory] = useState(false);

  const { data, isLoading, mutate } = useSWR(
    `discount-${store}-${brand}`,
    () => discountService.getDiscount({ store }),
    { revalidateOnFocus: false }
  );

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname
  );

  const {
    values,
    handleChange,
    dirty,
    handleSubmit,
    resetForm,
    setValues,
    isSubmitting,
  } = useFormik<DiscountsForm>({
    initialValues: formatDiscountsToForm(data),

    onSubmit: async (values) => {
      const {
        percentagePastSeasons,
        percentageTimelessSeasons,
        actualSeason,
        lastSeason,
      } = values;

      const customActualSeason = actualSeason.collections.map(
        ({ collection, discount }) => ({
          season: actualSeason.season,
          collection,
          discount: discount === '' ? 0 : Number(discount),
        })
      );

      const customLastSeason = lastSeason.collections.map(
        ({ collection, discount }) => ({
          season: lastSeason.season,
          collection,
          discount: discount === '' ? 0 : Number(discount),
        })
      );

      const newDiscounts = {
        percentagePastSeasons:
          percentagePastSeasons === '' ? 0 : Number(percentagePastSeasons),
        percentageTimelessSeasons:
          percentageTimelessSeasons === ''
            ? 0
            : Number(percentageTimelessSeasons),
        customDiscounts: [...customActualSeason, ...customLastSeason],
      };

      const response = await discountService.putDiscount({
        store,
        newDiscounts,
      });

      if (response.status === 200) {
        resetForm({ values });
        mutate();

        toast.success('Descontos alterados com sucesso', {
          delay: 500,
        });
      }
    },
  });

  useEffect(() => {
    if (!data || previousStore === store) return;

    resetForm({ values: formatDiscountsToForm(data) });

    setPreviousStore(store);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const { actualSeason, lastSeason } = data || {};

  function handleCloseModal() {
    blocker.reset && blocker.reset();
  }

  function handleConfirmLeaving() {
    blocker.proceed && blocker.proceed();
  }

  function changeAllDiscountsFromSeason(value: string) {
    if (!selectedSeason) return;

    if (!values[selectedSeason]) return;

    setValues({
      ...values,
      [selectedSeason]: {
        ...values[selectedSeason],
        collections: values[selectedSeason].collections.map((collection) => {
          return {
            ...collection,
            discount: value,
          };
        }),
      },
    });
  }

  return (
    <Main>
      {showHistory && (
        <DiscountHistory handleClose={() => setShowHistory(false)} />
      )}

      {Boolean(selectedSeason) && (
        <SeasonDiscountsModal
          handleClose={() => setSelectedSeason(null)}
          handleConfirm={changeAllDiscountsFromSeason}
        />
      )}

      {blocker.state === RouterBlocker.Blocked && (
        <DiscountWarningModal
          handleCloseModal={handleCloseModal}
          handleConfirmLeaving={handleConfirmLeaving}
        />
      )}

      <Header />

      <form
        onSubmit={handleSubmit}
        className="overflow-y-scroll p-3 pb-24 sm:px-12 sm:py-8 sm:pb-24"
        autoComplete="off"
      >
        <div className="flex w-full flex-col gap-10 lg:flex-row">
          <div className="flex flex-1 flex-col gap-6 xl:max-w-[640px]">
            <div className={CONTAINER_STYLE}>
              <span className="text-font16 font-bold">
                Configuração de desconto máximo aceito
              </span>

              <p className="text-font12">
                Configure os limites de desconto da sua loja de forma fácil.
                Utilize valores entre 0 e 99 para definir suas porcentagens,
                onde 0 significa nenhum desconto e 99 representa 99% de
                desconto. Ajuste conforme necessário para atender às suas
                estratégias de vendas.
              </p>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className={`${CONTAINER_STYLE} min-w-64 flex-1`}>
                <span className="text-font16 font-bold">Estações passadas</span>

                <div className="flex items-start gap-4">
                  <DiscountInput
                    isLoading={isLoading}
                    name="percentagePastSeasons"
                    value={values.percentagePastSeasons}
                    onChange={handleChange}
                  />

                  <p className="text-font12">
                    Desconto aplicado a estações anteriores.
                  </p>
                </div>
              </div>

              <div className={`${CONTAINER_STYLE} min-w-64 flex-1`}>
                <span className="text-font16 font-bold">
                  Estações atemporais
                </span>

                <div className="flex items-start gap-4">
                  <DiscountInput
                    name="percentageTimelessSeasons"
                    value={values.percentageTimelessSeasons}
                    onChange={handleChange}
                    isLoading={isLoading}
                  />

                  <p className="text-font12">
                    Desconto aplicado a estações permanentes.
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={() => setShowHistory(true)}
              variant="link"
              className="font-bold"
            >
              <AiOutlineEye size={20} />
              Histórico de alterações
            </Button>
          </div>

          {isLoading ? (
            <div className="flex w-full flex-1 items-center justify-center gap-6 xl:max-w-[640px]">
              <Loading />
            </div>
          ) : (
            <div className="flex flex-1 flex-col gap-6 xl:max-w-[640px]">
              <SeasonDiscountTable
                title={actualSeason?.name}
                values={values}
                handleChange={handleChange}
                seasonName={Seasons.ActualSeason}
                setSelectedSeason={setSelectedSeason}
                defaultOpen
              />

              <SeasonDiscountTable
                title={lastSeason?.name}
                values={values}
                handleChange={handleChange}
                seasonName={Seasons.LastSeason}
                setSelectedSeason={setSelectedSeason}
              />
            </div>
          )}
        </div>

        {dirty && <DiscountConfirm isSubmitting={isSubmitting} />}
      </form>
    </Main>
  );
}
